import Amplify, { API, Auth } from "aws-amplify";
import PropTypes from "prop-types";
import type { FC, ReactNode } from "react";
import { createContext, useEffect, useReducer } from "react";
import awsconfig from "../aws-exports";
import type { User } from "../types/user";

Amplify.configure(awsconfig);

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: User | null;
}

interface AuthContextValue extends State {
  platform: "Amplify";
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  verifyCode: (username: string, code: string) => Promise<void>;
  resendCode: (username: string) => Promise<void>;
  passwordRecovery: (username: string) => Promise<void>;
  passwordComplete: (username: any, password: string) => Promise<void>;
  passwordChange: (oldPassword: string, newPassword: string) => Promise<void>;
  passwordReset: (
    username: string,
    code: string,
    newPassword: string
  ) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: "INITIALIZE";
  payload: {
    isAuthenticated: boolean;
    user: User | null;
  };
};

type LoginAction = {
  type: "LOGIN";
  payload: {
    user: User;
  };
};
type LoginNewPasswordAction = {
  type: "LOGIN_NEW_PASSWORD_REQUIRED";
  payload: {
    amplifyUser: any;
  };
};

type LogoutAction = {
  type: "LOGOUT";
};

type RegisterAction = {
  type: "REGISTER";
};

type VerifyCodeAction = {
  type: "VERIFY_CODE";
};

type ResendCodeAction = {
  type: "RESEND_CODE";
};
type PasswordRecoveryAction = {
  type: "PASSWORD_RECOVERY";
};

type PasswordResetAction = {
  type: "PASSWORD_RESET";
};

type PasswordChangeAction = {
  type: "PASSWORD_CHANGE";
};

type Action =
  | InitializeAction
  | LoginAction
  | LogoutAction
  | RegisterAction
  | VerifyCodeAction
  | ResendCodeAction
  | PasswordRecoveryAction
  | PasswordResetAction
  | LoginNewPasswordAction
  | PasswordChangeAction;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers: Record<string, (state: State, action: Action) => State> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state: State, action: LoginAction): State => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state: State): State => ({ ...state }),
  VERIFY_CODE: (state: State): State => ({ ...state }),
  RESEND_CODE: (state: State): State => ({ ...state }),
  PASSWORD_RECOVERY: (state: State): State => ({ ...state }),
  PASSWORD_RESET: (state: State): State => ({ ...state }),
  PASSWORD_CHANGE: (state: State): State => ({ ...state }),
};

const reducer = (state: State, action: Action): State =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: "Amplify",
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  verifyCode: () => Promise.resolve(),
  resendCode: () => Promise.resolve(),
  passwordRecovery: () => Promise.resolve(),
  passwordReset: () => Promise.resolve(),
  passwordComplete: () => Promise.resolve(),
  passwordChange: () => Promise.resolve(),
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const paddlePlan = (await API.get("users", `/users/current`, {})) ?? {};

        const stripePlan =
          (await API.get("auth", `/auth/user/activePlan`, {})) ?? {};

        const plan = { ...stripePlan.plan, ...paddlePlan.plan };
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: true,
            user: {
              plan,
              id: user.sub,
              email: user.attributes.email,
              stripeUserId: user.attributes["custom:stripeCustomerId"],
              role: user.attributes["custom:role"],
              isTrialUsed: paddlePlan?.isTrialUsed,
              isOnboardingShown: paddlePlan?.isOnboardingShown,
            },
          },
        });
      } catch (error) {
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    const user = await Auth.signIn(email, password);
    if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
      dispatch({
        type: "LOGIN_NEW_PASSWORD_REQUIRED",
        payload: {
          amplifyUser: user,
        },
      });
      return user;
    }

    const paddlePlan = (await API.get("users", `/users/current`, {})) ?? {};

    const stripePlan =
      (await API.get("auth", `/auth/user/activePlan`, {})) ?? {};

    const plan = { ...stripePlan.plan, ...paddlePlan.plan };

    if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
      dispatch({
        type: "LOGIN_NEW_PASSWORD_REQUIRED",
        payload: {
          amplifyUser: user,
        },
      });
      return user;
    }

    if (user.challengeName) {
      console.error(
        `Unable to login, because challenge "${user.challengeName}" is mandated and we did not handle this case.`
      );
      return;
    }

    dispatch({
      type: "LOGIN",
      payload: {
        user: {
          plan,
          id: user.attributes.sub,
          email: user.attributes.email,
          stripeUserId: user.attributes["custom:stripeCustomerId"],
          role: user.attributes["custom:role"],
          isTrialUsed: paddlePlan?.isTrialUsed,
          isOnboardingShown: paddlePlan?.isOnboardingShown,
        },
      },
    });
  };

  const logout = async (): Promise<void> => {
    await Auth.signOut();
    dispatch({
      type: "LOGOUT",
    });
  };

  const register = async (email: string, password: string): Promise<void> => {
    await Auth.signUp({
      username: email,
      password,
      attributes: { email },
    });
    dispatch({
      type: "REGISTER",
    });
  };

  const verifyCode = async (username: string, code: string): Promise<void> => {
    await Auth.confirmSignUp(username, code);
    dispatch({
      type: "VERIFY_CODE",
    });
  };

  const resendCode = async (username: string): Promise<void> => {
    await Auth.resendSignUp(username);
    dispatch({
      type: "RESEND_CODE",
    });
  };

  const passwordRecovery = async (username: string): Promise<void> => {
    await Auth.forgotPassword(username);
    dispatch({
      type: "PASSWORD_RECOVERY",
    });
  };
  const passwordComplete = async (
    user: any,
    newPassword: string
  ): Promise<void> => {
    await Auth.completeNewPassword(user, newPassword);
    const loggedUser = await Auth.currentAuthenticatedUser();
    let { plan } = await API.get("users", `/users/current`, {});
    if (!plan) {
      const stripePlan = await API.get("auth", `/auth/user/activePlan`, {});
      plan = stripePlan.plan;
    }
    dispatch({
      type: "LOGIN",
      payload: {
        user: {
          plan,
          id: loggedUser.attributes.sub,
          email: loggedUser.attributes.email,
          isTrialUsed: loggedUser?.isTrialUsed,
          isOnboardingShown: loggedUser?.isOnboardingShown,
        },
      },
    });
  };

  const passwordReset = async (
    username: string,
    code: string,
    newPassword: string
  ): Promise<void> => {
    await Auth.forgotPasswordSubmit(username, code, newPassword);
    dispatch({
      type: "PASSWORD_RESET",
    });
  };

  const passwordChange = async (
    oldPassword: string,
    newPassword: string
  ): Promise<void> => {
    const loggedUser = await Auth.currentAuthenticatedUser();
    await Auth.changePassword(loggedUser, oldPassword, newPassword);
    dispatch({
      type: "PASSWORD_CHANGE",
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: "Amplify",
        login,
        logout,
        register,
        verifyCode,
        resendCode,
        passwordRecovery,
        passwordReset,
        passwordComplete,
        passwordChange,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const AuthConsumer = AuthContext.Consumer;
